// src/importes.ts
var ImporteInvalidoError = class extends Error {
  constructor(valor, motivo) {
    super(`Importe no v\xE1lido (${String(valor)}): ${motivo}`);
    this.name = "ImporteInvalidoError";
  }
};
function esCentimos(valor) {
  return typeof valor === "number" && Number.isSafeInteger(valor);
}
function aCentimos(valor) {
  let texto;
  if (typeof valor === "number") {
    if (!Number.isFinite(valor)) throw new ImporteInvalidoError(valor, "no es un n\xFAmero finito");
    texto = String(valor);
    if (/e/i.test(texto)) texto = valor.toFixed(10);
  } else {
    texto = valor.replace(/[€\s ]/g, "");
    const tienePunto = texto.includes(".");
    const tieneComa = texto.includes(",");
    if (tienePunto && tieneComa) {
      if (texto.lastIndexOf(",") > texto.lastIndexOf(".")) {
        texto = texto.replace(/\./g, "").replace(",", ".");
      } else {
        texto = texto.replace(/,/g, "");
      }
    } else if (tieneComa) {
      if ((texto.match(/,/g) ?? []).length > 1) throw new ImporteInvalidoError(valor, "varias comas");
      texto = texto.replace(",", ".");
    } else if ((texto.match(/\./g) ?? []).length > 1) {
      texto = texto.replace(/\./g, "");
    }
  }
  const m = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(texto);
  if (!m || m[2] === "" && (m[3] ?? "") === "") {
    throw new ImporteInvalidoError(valor, "formato no reconocido");
  }
  const negativo = m[1] === "-";
  const entero = m[2] === "" ? "0" : m[2];
  const fraccion = (m[3] ?? "").padEnd(3, "0");
  let centimos = BigInt(entero) * 100n + BigInt(fraccion.slice(0, 2));
  if (Number(fraccion[2]) >= 5) centimos += 1n;
  if (centimos > BigInt(Number.MAX_SAFE_INTEGER)) throw new ImporteInvalidoError(valor, "demasiado grande");
  const resultado = Number(centimos);
  return negativo && resultado !== 0 ? -resultado : resultado;
}
function aEuros(centimos) {
  return centimos / 100;
}
var formatoEuros = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2
});
function formatearEuros(centimos) {
  return formatoEuros.format(centimos / 100);
}
function sumar(importes) {
  let total = 0;
  for (const i of importes) total += i;
  return total;
}
function aplicarPorcentaje(importe, porcentaje) {
  const producto = BigInt(importe) * BigInt(porcentaje);
  const divisor = 10000n;
  const cociente = producto / divisor;
  const resto = producto % divisor;
  const absResto = resto < 0n ? -resto : resto;
  let resultado = cociente;
  if (absResto * 2n >= divisor) resultado += producto < 0n ? -1n : 1n;
  return Number(resultado);
}
function aPuntosBasicos(porcentaje) {
  return aCentimos(porcentaje);
}

// src/fechas.ts
var RE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
function esFechaISO(valor) {
  if (typeof valor !== "string") return false;
  const m = RE_ISO.exec(valor);
  if (!m) return false;
  const [a, me, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const fecha2 = new Date(Date.UTC(a, me - 1, d));
  return fecha2.getUTCFullYear() === a && fecha2.getUTCMonth() === me - 1 && fecha2.getUTCDate() === d;
}
function partes(fecha2) {
  if (!esFechaISO(fecha2)) throw new Error(`Fecha no v\xE1lida: ${String(fecha2)}`);
  const [a, m, d] = fecha2.split("-").map(Number);
  return [a, m, d];
}
function crearFecha(anio, mes, dia) {
  const f = `${String(anio).padStart(4, "0")}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
  if (!esFechaISO(f)) throw new Error(`Fecha no v\xE1lida: ${f}`);
  return f;
}
function ejercicioDe(fecha2) {
  return partes(fecha2)[0];
}
function trimestreDe(fecha2) {
  return Math.floor((partes(fecha2)[1] - 1) / 3) + 1;
}
function periodoDe(fecha2) {
  return `${trimestreDe(fecha2)}T`;
}
function periodoDeTrimestre(t) {
  return `${t}T`;
}
function inicioTrimestre(ejercicio, t) {
  return crearFecha(ejercicio, (t - 1) * 3 + 1, 1);
}
function finTrimestre(ejercicio, t) {
  const mes = t * 3;
  return crearFecha(ejercicio, mes, diasDelMes(ejercicio, mes));
}
function diasDelMes(anio, mes) {
  return new Date(Date.UTC(anio, mes, 0)).getUTCDate();
}
function diasDelAnio(anio) {
  return anio % 4 === 0 && anio % 100 !== 0 || anio % 400 === 0 ? 366 : 365;
}
function compararFechas(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}
function sumarDias(fecha2, dias) {
  const [a, m, d] = partes(fecha2);
  const f = new Date(Date.UTC(a, m - 1, d + dias));
  return crearFecha(f.getUTCFullYear(), f.getUTCMonth() + 1, f.getUTCDate());
}
function diasEntre(desde, hasta) {
  const [a1, m1, d1] = partes(desde);
  const [a2, m2, d2] = partes(hasta);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 864e5);
}
function diaSemana(fecha2) {
  const [a, m, d] = partes(fecha2);
  return new Date(Date.UTC(a, m - 1, d)).getUTCDay();
}
function trasladarFinDeSemana(fecha2) {
  const dia = diaSemana(fecha2);
  if (dia === 6) return sumarDias(fecha2, 2);
  if (dia === 0) return sumarDias(fecha2, 1);
  return fecha2;
}
function desdeDDMMAAAA(texto) {
  const m = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/.exec(texto);
  if (!m) return null;
  const f = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return esFechaISO(f) ? f : null;
}
function aDDMMAAAA(fecha2) {
  const [a, m, d] = partes(fecha2);
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${a}`;
}
function desdeSerieExcel(serie) {
  if (!Number.isFinite(serie) || serie < 1 || serie > 2958465) return null;
  const ms = Date.UTC(1899, 11, 30) + Math.floor(serie) * 864e5;
  const f = new Date(ms);
  return crearFecha(f.getUTCFullYear(), f.getUTCMonth() + 1, f.getUTCDate());
}
function hoy(zona = "Atlantic/Canary", ahora = /* @__PURE__ */ new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zona,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(ahora);
}

// src/identificacion.ts
var CLAVE_TIPO_IDENTIFICACION = {
  NIF: "01",
  NIF_IVA: "02",
  PASAPORTE: "03",
  DOC_PAIS_RESIDENCIA: "04",
  CERT_RESIDENCIA: "05",
  OTRO: "06"
};
var LETRAS_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
var LETRAS_CONTROL_PJ = "JABCDEFGHI";
function normalizarIdentificador(valor) {
  return valor.toUpperCase().replace(/[\s.\-_/]/g, "");
}
function letraDni(numero) {
  return LETRAS_DNI[numero % 23];
}
function validarNifEspanol(valor) {
  let n = normalizarIdentificador(valor);
  if (n.startsWith("ES") && n.length === 11) n = n.slice(2);
  if (n.length !== 9) {
    return { valido: false, normalizado: n, motivo: "Debe tener 9 caracteres" };
  }
  if (/^\d{8}[A-Z]$/.test(n)) {
    const ok = letraDni(Number(n.slice(0, 8))) === n[8];
    return ok ? { valido: true, normalizado: n, clase: "DNI" } : { valido: false, normalizado: n, clase: "DNI", motivo: "La letra de control no coincide" };
  }
  if (/^[XYZ]\d{7}[A-Z]$/.test(n)) {
    const prefijo = { X: "0", Y: "1", Z: "2" }[n[0]];
    const ok = letraDni(Number(prefijo + n.slice(1, 8))) === n[8];
    return ok ? { valido: true, normalizado: n, clase: "NIE" } : { valido: false, normalizado: n, clase: "NIE", motivo: "La letra de control no coincide" };
  }
  if (/^[KLM]\d{7}[A-Z]$/.test(n)) {
    const ok = letraDni(Number(n.slice(1, 8))) === n[8];
    return ok ? { valido: true, normalizado: n, clase: "NIF_KLM" } : { valido: false, normalizado: n, clase: "NIF_KLM", motivo: "La letra de control no coincide" };
  }
  if (/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(n)) {
    const digitos = n.slice(1, 8);
    let suma = 0;
    for (let i = 0; i < 7; i++) {
      const d = Number(digitos[i]);
      if (i % 2 === 0) {
        const doble = d * 2;
        suma += Math.floor(doble / 10) + doble % 10;
      } else {
        suma += d;
      }
    }
    const digitoControl = (10 - suma % 10) % 10;
    const letraControl = LETRAS_CONTROL_PJ[digitoControl];
    const control = n[8];
    const inicial = n[0];
    let ok;
    if ("PQRSNW".includes(inicial)) ok = control === letraControl;
    else if ("ABEH".includes(inicial)) ok = control === String(digitoControl);
    else ok = control === letraControl || control === String(digitoControl);
    return ok ? { valido: true, normalizado: n, clase: "PERSONA_JURIDICA" } : { valido: false, normalizado: n, clase: "PERSONA_JURIDICA", motivo: "El car\xE1cter de control no coincide" };
  }
  return { valido: false, normalizado: n, motivo: "Formato no reconocido" };
}
var PREFIJOS_IVA_UE = /* @__PURE__ */ new Set([
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "EL",
  "ES",
  "FI",
  "FR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "XI",
  "EU"
]);
function validarNifIva(valor) {
  const n = normalizarIdentificador(valor);
  const prefijo = n.slice(0, 2);
  if (!PREFIJOS_IVA_UE.has(prefijo)) {
    return { valido: false, normalizado: n, motivo: `Prefijo de pa\xEDs "${prefijo}" no reconocido` };
  }
  if (prefijo === "ES") {
    const r = validarNifEspanol(n.slice(2));
    return { ...r, normalizado: n };
  }
  if (!/^[A-Z]{2}[0-9A-Z+*]{2,13}$/.test(n)) {
    return { valido: false, normalizado: n, motivo: "Formato de NIF-IVA no v\xE1lido" };
  }
  return { valido: true, normalizado: n };
}
function validarIdentificacion(tipo, valor) {
  if (tipo === "NIF") return validarNifEspanol(valor);
  if (tipo === "NIF_IVA") return validarNifIva(valor);
  const n = normalizarIdentificador(valor);
  if (n.length === 0 || n.length > 20) {
    return { valido: false, normalizado: n, motivo: "Debe tener entre 1 y 20 caracteres" };
  }
  return { valido: true, normalizado: n };
}

// src/catalogos.ts
function catalogo(lista) {
  const porCodigo = new Map(lista.map((e) => [e.codigo, e]));
  return {
    lista,
    codigos: lista.map((e) => e.codigo),
    nombre: (codigo) => porCodigo.get(codigo)?.nombre,
    existe: (codigo) => porCodigo.has(codigo)
  };
}
var TIPOS_FACTURA_EMITIDA = catalogo([
  { codigo: "SF", nombre: "Venta o ingreso sin obligaci\xF3n de emitir factura" },
  { codigo: "F1", nombre: "Factura con destinatario identificado" },
  { codigo: "F2", nombre: "Factura simplificada (sin identificar al destinatario)" },
  { codigo: "F3", nombre: "Factura que sustituye a facturas simplificadas" },
  { codigo: "F4", nombre: "Asiento resumen de facturas" },
  { codigo: "R1", nombre: "Rectificativa (error fundado en derecho y otras causas)" },
  { codigo: "R2", nombre: "Rectificativa (concurso de acreedores)" },
  { codigo: "R3", nombre: "Rectificativa (cr\xE9dito incobrable)" },
  { codigo: "R4", nombre: "Rectificativa (resto)" },
  { codigo: "R5", nombre: "Rectificativa de factura simplificada" }
]);
var TIPOS_FACTURA_RECIBIDA = catalogo([
  { codigo: "F1", nombre: "Factura completa" },
  { codigo: "F2", nombre: "Factura simplificada (ticket)" },
  { codigo: "F3", nombre: "Factura que sustituye a simplificadas" },
  { codigo: "F4", nombre: "Asiento resumen de facturas" },
  { codigo: "F5", nombre: "Importaci\xF3n (DUA)" },
  { codigo: "F6", nombre: "Justificante contable (recibo, etc.)" },
  { codigo: "R1", nombre: "Rectificativa (error fundado en derecho y otras causas)" },
  { codigo: "R2", nombre: "Rectificativa (concurso de acreedores)" },
  { codigo: "R3", nombre: "Rectificativa (cr\xE9dito incobrable)" },
  { codigo: "R4", nombre: "Rectificativa (resto)" },
  { codigo: "R5", nombre: "Rectificativa de factura simplificada" },
  { codigo: "LC", nombre: "Aduanas: liquidaci\xF3n complementaria" },
  { codigo: "SF", nombre: "Gasto sin factura (amortizaciones, provisiones\u2026)" }
]);
var CONCEPTOS_INGRESO = catalogo([
  { codigo: "I01", nombre: "Ingresos de explotaci\xF3n" },
  { codigo: "I02", nombre: "Ingresos financieros por aplazamiento de operaciones de la actividad" },
  { codigo: "I03", nombre: "Subvenciones corrientes" },
  { codigo: "I04", nombre: "Imputaci\xF3n de subvenciones de capital" },
  { codigo: "I05", nombre: "IVA devengado (recargo de equivalencia / REAGP)" },
  { codigo: "I06", nombre: "Variaci\xF3n de existencias (incremento)" },
  { codigo: "I07", nombre: "Otros ingresos" },
  { codigo: "I08", nombre: "Autoconsumo de bienes y servicios" },
  { codigo: "I09", nombre: "Exceso de amortizaci\xF3n por libertad de amortizaci\xF3n" }
]);
var CONCEPTOS_GASTO = catalogo([
  { codigo: "G01", nombre: "Compra de existencias (materias primas, mercader\xEDas)" },
  { codigo: "G02", nombre: "Variaci\xF3n de existencias (disminuci\xF3n)" },
  { codigo: "G03", nombre: "Otros consumos de explotaci\xF3n" },
  { codigo: "G04", nombre: "Sueldos y salarios" },
  { codigo: "G05", nombre: "Seguridad Social a cargo de la empresa" },
  { codigo: "G45", nombre: "Seguridad Social del titular" },
  { codigo: "G46", nombre: "Mutualidad alternativa del titular" },
  { codigo: "G06", nombre: "Seguridad Social y mutualidades del titular (hasta 1T 2024)" },
  { codigo: "G07", nombre: "Indemnizaciones" },
  { codigo: "G08", nombre: "Dietas y viajes del personal" },
  { codigo: "G09", nombre: "Previsi\xF3n social del personal" },
  { codigo: "G10", nombre: "Otros gastos de personal" },
  { codigo: "G11", nombre: "Manutenci\xF3n del titular" },
  { codigo: "G12", nombre: "Arrendamientos y c\xE1nones (alquiler de puesto, local\u2026)" },
  { codigo: "G13", nombre: "Reparaciones y conservaci\xF3n" },
  { codigo: "GY4", nombre: "Suministros (luz, agua, gas, tel\xE9fono, internet)" },
  { codigo: "G14", nombre: "Suministro el\xE9ctrico" },
  { codigo: "G15", nombre: "Suministro de agua" },
  { codigo: "G16", nombre: "Suministro de gas" },
  { codigo: "G17", nombre: "Telefon\xEDa e internet" },
  { codigo: "G18", nombre: "Otros suministros" },
  { codigo: "G19", nombre: "Servicios de profesionales independientes" },
  { codigo: "G40", nombre: "Formalizaci\xF3n de contratos" },
  { codigo: "G41", nombre: "Defensa jur\xEDdica" },
  { codigo: "G42", nombre: "Otros servicios personales de terceros" },
  { codigo: "G20", nombre: "Primas de seguros" },
  { codigo: "G22", nombre: "Otros servicios exteriores (software, publicidad, comisiones\u2026)" },
  { codigo: "G44", nombre: "Gastos de comunidad" },
  { codigo: "G23", nombre: "Intereses de deudas" },
  { codigo: "G24", nombre: "Otros gastos financieros" },
  { codigo: "G25", nombre: "IVA soportado (recargo de equivalencia / REAGP)" },
  { codigo: "G26", nombre: "Otros tributos deducibles" },
  { codigo: "GY8", nombre: "Amortizaci\xF3n del inmovilizado material" },
  { codigo: "G27", nombre: "Amortizaci\xF3n de edificios" },
  { codigo: "G28", nombre: "Amortizaci\xF3n de instalaciones y mobiliario" },
  { codigo: "G29", nombre: "Amortizaci\xF3n de maquinaria" },
  { codigo: "G30", nombre: "Amortizaci\xF3n de elementos de transporte" },
  { codigo: "G31", nombre: "Amortizaci\xF3n de equipos inform\xE1ticos" },
  { codigo: "G32", nombre: "Amortizaci\xF3n de \xFAtiles y herramientas" },
  { codigo: "G33", nombre: "Amortizaci\xF3n de ganado y cultivos" },
  { codigo: "G38", nombre: "Amortizaci\xF3n del inmovilizado inmaterial" },
  { codigo: "G34", nombre: "P\xE9rdidas por insolvencias" },
  { codigo: "G35", nombre: "Mecenazgo: convenios de colaboraci\xF3n" },
  { codigo: "G36", nombre: "Mecenazgo: gastos en actividades de inter\xE9s general" },
  { codigo: "G43", nombre: "Saldos de dudoso cobro" },
  { codigo: "G37", nombre: "Otros conceptos fiscalmente deducibles" },
  { codigo: "G39", nombre: "Provisiones fiscalmente deducibles" },
  { codigo: "G47", nombre: "Regularizaci\xF3n RETA (a ingresar)" },
  { codigo: "G48", nombre: "Regularizaci\xF3n RETA (devoluci\xF3n)" }
]);
var CODIGOS_ACTIVIDAD = catalogo([
  { codigo: "A", nombre: "Actividades incluidas en el IAE" },
  { codigo: "B", nombre: "Actividades no incluidas en el IAE" },
  { codigo: "C", nombre: "Actividades no iniciadas" },
  { codigo: "D", nombre: "Arrendadores de inmuebles no incluidos en los anteriores" }
]);
var TIPOS_ACTIVIDAD_A = catalogo([
  { codigo: "01", nombre: "Arrendadores de bienes inmuebles" },
  { codigo: "02", nombre: "Ganader\xEDa independiente" },
  { codigo: "03", nombre: "Resto de actividades empresariales" },
  { codigo: "04", nombre: "Actividades profesionales de car\xE1cter art\xEDstico o deportivo" },
  { codigo: "05", nombre: "Resto de actividades profesionales" }
]);
function esActividadProfesional(codigo, tipo) {
  return codigo === "A" && (tipo === "04" || tipo === "05");
}
var FORMAS_PAGO = catalogo([
  { codigo: "efectivo", nombre: "Efectivo" },
  { codigo: "tarjeta", nombre: "Tarjeta" },
  { codigo: "bizum", nombre: "Bizum" },
  { codigo: "transferencia", nombre: "Transferencia" },
  { codigo: "domiciliacion", nombre: "Domiciliaci\xF3n bancaria" },
  { codigo: "paypal", nombre: "PayPal" },
  { codigo: "otro", nombre: "Otro" }
]);
function interpretarFormaPago(texto) {
  if (!texto) return null;
  const t = texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  if (!t) return null;
  if (t.startsWith("efect") || t === "cash" || t === "metalico") return "efectivo";
  if (t.startsWith("tarj") || t.includes("datafono") || t.includes("sumup") || t === "tpv" || t === "card") return "tarjeta";
  if (t.startsWith("bizu") || t.startsWith("bisu")) return "bizum";
  if (t.startsWith("transf")) return "transferencia";
  if (t.startsWith("domicil") || t.includes("recibo")) return "domiciliacion";
  if (t.startsWith("paypal")) return "paypal";
  return "otro";
}
var CATEGORIAS_DOCUMENTO = catalogo([
  { codigo: "justificante_gasto", nombre: "Justificante o factura de gasto" },
  { codigo: "factura_emitida", nombre: "Factura emitida" },
  { codigo: "alta_iae", nombre: "Alta en el IAE / censal AEAT" },
  { codigo: "declaracion_censal", nombre: "Declaraci\xF3n censal ATC (modelo 400)" },
  { codigo: "certificado", nombre: "Certificado (digital, de estar al corriente\u2026)" },
  { codigo: "seguro", nombre: "Seguro" },
  { codigo: "licencia", nombre: "Licencia o autorizaci\xF3n" },
  { codigo: "permiso_mercadillo", nombre: "Permiso de mercadillo / feria" },
  { codigo: "contrato", nombre: "Contrato" },
  { codigo: "declaracion_presentada", nombre: "Justificante de declaraci\xF3n presentada" },
  { codigo: "banco", nombre: "Documento bancario" },
  { codigo: "otro", nombre: "Otro" }
]);

// src/normativa.ts
var F = {
  instr130: "https://sede.agenciatributaria.gob.es/Sede/impuestos-tasas/impuesto-sobre-renta-personas-fisicas/modelo-130-irpf______esionales-estimacion-directa-fraccionado_/instrucciones.html",
  eds: "https://sede.agenciatributaria.gob.es/Sede/irpf/empresarios-individuales-profesionales/regimenes-determinar-rendimiento-actividad/estimacion-directa-simplificada.html",
  repepFaq: "https://www3.gobiernodecanarias.org/tributos/atc/estatico/asistencia_contribuyente/pdf/preguntas_frecuentes_REPEP.pdf",
  dl3_2026: "https://www.gobiernodecanarias.org/boc/2026/066/1094.html",
  smi2026: "RD 126/2026 (BOE-A-2026-3815)",
  retenciones: "https://sede.agenciatributaria.gob.es/Sede/ayuda/manuales-videos-folletos/manuales-practicos/folleto-actividades-economicas/7-otras-obligaciones-fiscales-retenciones/7_1-cuadro-relacion-tipos-retencion-porcentaje.html",
  conservacion: "Arts. 66-70 LGT (4 a\xF1os) y art. 30 C\xF3digo de Comercio (6 a\xF1os)",
  verifactu: "https://sede.agenciatributaria.gob.es/Sede/iva/sistemas-informaticos-facturacion-verifactu/preguntas-frecuentes/cuestiones-generales-ambitos-aplicacion.html"
};
var VERIFICADO = "2026-10-19";
var MINORACION_130 = [
  { hasta: 9e5, importe: 1e4 },
  { hasta: 1e6, importe: 7500 },
  { hasta: 11e5, importe: 5e3 },
  { hasta: 12e5, importe: 2500 }
];
var NORMATIVA_2026 = {
  ejercicio: 2026,
  umbralRepep: { valor: 3e6, fuente: F.repepFaq, verificado: VERIFICADO },
  smiAnual: { valor: 1709400, fuente: F.smi2026, verificado: VERIFICADO },
  pago130: {
    valor: {
      general: 2e3,
      excepciones: [
        { ambito: "ceuta_melilla", trimestres: [1, 2, 3, 4], porcentaje: 800 },
        { ambito: "la_palma", trimestres: [3, 4], porcentaje: 800 }
      ]
    },
    fuente: F.instr130,
    verificado: VERIFICADO
  },
  dificilJustificacion: { valor: { porcentaje: 500, limiteAnual: 2e5 }, fuente: F.eds, verificado: VERIFICADO },
  minoracion130: { valor: MINORACION_130, fuente: F.instr130, verificado: VERIFICADO },
  retencionProfesional: {
    valor: { general: 1500, inicioActividad: 700 },
    fuente: F.retenciones,
    verificado: VERIFICADO,
    provisional: true
    // pendiente de comprobar en el cuadro oficial de 2026
  },
  limiteOperacionesTerceros: { valor: 300506, fuente: "Modelo 347 AEAT", verificado: VERIFICADO, provisional: true },
  conservacionAnios: { valor: 6, fuente: F.conservacion, verificado: VERIFICADO },
  verifactuObligatorio: { valor: "2027-07-01", fuente: F.verifactu, verificado: VERIFICADO }
};
var NORMATIVA_2027 = {
  ...NORMATIVA_2026,
  ejercicio: 2027,
  umbralRepep: { valor: 5e6, fuente: F.dl3_2026, verificado: VERIFICADO },
  smiAnual: { ...NORMATIVA_2026.smiAnual, provisional: true },
  pago130: {
    valor: { general: 2e3, excepciones: [{ ambito: "ceuta_melilla", trimestres: [1, 2, 3, 4], porcentaje: 800 }] },
    fuente: F.instr130,
    verificado: VERIFICADO,
    provisional: true
  },
  dificilJustificacion: { ...NORMATIVA_2026.dificilJustificacion, provisional: true },
  minoracion130: { ...NORMATIVA_2026.minoracion130, provisional: true }
};
var NORMATIVAS = /* @__PURE__ */ new Map([
  [2026, NORMATIVA_2026],
  [2027, NORMATIVA_2027]
]);
var EJERCICIOS_CON_NORMATIVA = [...NORMATIVAS.keys()].sort();
function normativaDe(ejercicio) {
  const exacta = NORMATIVAS.get(ejercicio);
  if (exacta) return { ...exacta, aproximada: false };
  const anteriores = EJERCICIOS_CON_NORMATIVA.filter((e) => e < ejercicio);
  const base = NORMATIVAS.get(anteriores.length ? anteriores[anteriores.length - 1] : EJERCICIOS_CON_NORMATIVA[0]);
  return { ...base, ejercicio, aproximada: true };
}
function minoracion130(tramos, rendimientoAnterior) {
  const r = rendimientoAnterior ?? 0;
  for (const t of tramos) if (r <= t.hasta) return t.importe;
  return 0;
}
var unParametro = (p) => p;
var PARAMETROS_EDITABLES = [
  {
    clave: "umbralRepep",
    nombre: "L\xEDmite del REPEP",
    ayuda: "Volumen de operaciones del a\xF1o anterior hasta el que se puede estar en el r\xE9gimen del peque\xF1o empresario.",
    tipo: "centimos",
    leer: (n) => unParametro(n.umbralRepep),
    aplicar: (n, v) => ({ ...n, umbralRepep: { ...n.umbralRepep, valor: v } })
  },
  {
    clave: "smiAnual",
    nombre: "Salario m\xEDnimo interprofesional anual",
    ayuda: "Referencia del indicador (informativo) de habitualidad para el alta en el RETA.",
    tipo: "centimos",
    leer: (n) => unParametro(n.smiAnual),
    aplicar: (n, v) => ({ ...n, smiAnual: { ...n.smiAnual, valor: v } })
  },
  {
    clave: "pago130General",
    nombre: "Porcentaje del pago fraccionado (modelo 130)",
    ayuda: "Casilla 04: porcentaje del rendimiento neto acumulado (20 % general).",
    tipo: "puntosBasicos",
    leer: (n) => unParametro({ ...n.pago130, valor: n.pago130.valor.general }),
    aplicar: (n, v) => ({ ...n, pago130: { ...n.pago130, valor: { ...n.pago130.valor, general: v } } })
  },
  {
    clave: "dificilJustificacionPorcentaje",
    nombre: "Gastos de dif\xEDcil justificaci\xF3n: porcentaje",
    ayuda: "Estimaci\xF3n directa simplificada: porcentaje del rendimiento neto positivo (5 % general).",
    tipo: "puntosBasicos",
    leer: (n) => unParametro({ ...n.dificilJustificacion, valor: n.dificilJustificacion.valor.porcentaje }),
    aplicar: (n, v) => ({
      ...n,
      dificilJustificacion: { ...n.dificilJustificacion, valor: { ...n.dificilJustificacion.valor, porcentaje: v } }
    })
  },
  {
    clave: "dificilJustificacionLimite",
    nombre: "Gastos de dif\xEDcil justificaci\xF3n: l\xEDmite anual",
    ayuda: "M\xE1ximo anual de la provisi\xF3n (2.000 \u20AC general).",
    tipo: "centimos",
    leer: (n) => unParametro({ ...n.dificilJustificacion, valor: n.dificilJustificacion.valor.limiteAnual }),
    aplicar: (n, v) => ({
      ...n,
      dificilJustificacion: { ...n.dificilJustificacion, valor: { ...n.dificilJustificacion.valor, limiteAnual: v } }
    })
  },
  {
    clave: "retencionProfesionalGeneral",
    nombre: "Retenci\xF3n de IRPF en facturas profesionales (general)",
    ayuda: "Porcentaje que se retiene al facturar a empresas o profesionales (15 % general).",
    tipo: "puntosBasicos",
    leer: (n) => unParametro({ ...n.retencionProfesional, valor: n.retencionProfesional.valor.general }),
    aplicar: (n, v) => ({
      ...n,
      retencionProfesional: { ...n.retencionProfesional, valor: { ...n.retencionProfesional.valor, general: v } }
    })
  },
  {
    clave: "retencionProfesionalInicio",
    nombre: "Retenci\xF3n de IRPF en facturas profesionales (inicio de actividad)",
    ayuda: "Porcentaje reducido el a\xF1o de inicio y los dos siguientes (7 %).",
    tipo: "puntosBasicos",
    leer: (n) => unParametro({ ...n.retencionProfesional, valor: n.retencionProfesional.valor.inicioActividad }),
    aplicar: (n, v) => ({
      ...n,
      retencionProfesional: { ...n.retencionProfesional, valor: { ...n.retencionProfesional.valor, inicioActividad: v } }
    })
  },
  {
    clave: "limiteOperacionesTerceros",
    nombre: "L\xEDmite anual de operaciones con terceros (347 / 415)",
    ayuda: "Importe anual con un mismo cliente o proveedor a partir del cual hay que declararlo.",
    tipo: "centimos",
    leer: (n) => unParametro(n.limiteOperacionesTerceros),
    aplicar: (n, v) => ({ ...n, limiteOperacionesTerceros: { ...n.limiteOperacionesTerceros, valor: v } })
  },
  {
    clave: "conservacionAnios",
    nombre: "A\xF1os de conservaci\xF3n de documentos",
    ayuda: "M\xEDnimo recomendado: 6 a\xF1os desde el cierre del ejercicio (4 tributarios y 6 mercantiles).",
    tipo: "entero",
    leer: (n) => unParametro(n.conservacionAnios),
    aplicar: (n, v) => ({ ...n, conservacionAnios: { ...n.conservacionAnios, valor: v } })
  },
  {
    clave: "minoracion130",
    nombre: "Minoraci\xF3n del modelo 130 por rendimientos bajos",
    ayuda: "Casilla 13 (art. 110.3.c RIRPF): importe por trimestre seg\xFAn el rendimiento neto del a\xF1o anterior.",
    tipo: "tramos",
    leer: (n) => unParametro(n.minoracion130),
    aplicar: (n, v) => ({ ...n, minoracion130: { ...n.minoracion130, valor: v } })
  }
];
var descriptorParametro = (clave) => PARAMETROS_EDITABLES.find((p) => p.clave === clave);
function validarValorParametro(clave, valor) {
  const d = descriptorParametro(clave);
  if (!d) return { ok: false, error: `Par\xE1metro desconocido: ${clave}` };
  const entero = (v, min, max, que) => typeof v === "number" && Number.isSafeInteger(v) && v >= min && v <= max ? { ok: true, valor: v } : { ok: false, error: `${que} debe ser un n\xFAmero entero entre ${min} y ${max}` };
  switch (d.tipo) {
    case "centimos":
      return entero(valor, 0, 1e10, "El importe (en c\xE9ntimos)");
    case "puntosBasicos":
      return entero(valor, 0, 1e4, "El porcentaje (en puntos b\xE1sicos: 2000 = 20 %)");
    case "entero":
      return entero(valor, 1, 30, "El n\xFAmero de a\xF1os");
    case "tramos": {
      if (!Array.isArray(valor) || valor.length === 0 || valor.length > 10) {
        return { ok: false, error: "Indica entre 1 y 10 tramos" };
      }
      let anterior = -Infinity;
      for (const t of valor) {
        const h = entero(t?.hasta, -1e10, 1e10, "El l\xEDmite del tramo");
        const i = entero(t?.importe, 0, 1e10, "El importe del tramo");
        if (!h.ok) return h;
        if (!i.ok) return i;
        if (t.hasta <= anterior) return { ok: false, error: "Los tramos deben estar ordenados de menor a mayor" };
        anterior = t.hasta;
      }
      return { ok: true, valor: valor.map((t) => ({ hasta: t.hasta, importe: t.importe })) };
    }
  }
}
function aplicarValoresEditados(base, editados) {
  let n = base;
  for (const e of editados) {
    const d = descriptorParametro(e.parametro);
    if (!d) continue;
    const v = validarValorParametro(e.parametro, e.valor);
    if (!v.ok) continue;
    n = d.aplicar(n, v.valor);
    n = marcarEditado(n, e.parametro);
  }
  return n;
}
function marcarEditado(n, clave) {
  const marca = { fuente: "Valor introducido por el negocio", provisional: false };
  switch (clave) {
    case "umbralRepep":
      return { ...n, umbralRepep: { ...n.umbralRepep, ...marca } };
    case "smiAnual":
      return { ...n, smiAnual: { ...n.smiAnual, ...marca } };
    case "pago130General":
      return { ...n, pago130: { ...n.pago130, ...marca } };
    case "dificilJustificacionPorcentaje":
    case "dificilJustificacionLimite":
      return { ...n, dificilJustificacion: { ...n.dificilJustificacion, ...marca } };
    case "retencionProfesionalGeneral":
    case "retencionProfesionalInicio":
      return { ...n, retencionProfesional: { ...n.retencionProfesional, ...marca } };
    case "limiteOperacionesTerceros":
      return { ...n, limiteOperacionesTerceros: { ...n.limiteOperacionesTerceros, ...marca } };
    case "conservacionAnios":
      return { ...n, conservacionAnios: { ...n.conservacionAnios, ...marca } };
    case "minoracion130":
      return { ...n, minoracion130: { ...n.minoracion130, ...marca } };
    default:
      return n;
  }
}
function tablaParametros(ejercicio, editados) {
  const base = normativaDe(ejercicio);
  const aplicada = aplicarValoresEditados(base, editados);
  return PARAMETROS_EDITABLES.map((d) => {
    const b = d.leer(base);
    const a = d.leer(aplicada);
    const e = editados.find((x) => x.parametro === d.clave);
    return {
      clave: d.clave,
      nombre: d.nombre,
      ayuda: d.ayuda,
      tipo: d.tipo,
      porDefecto: b.valor,
      actual: a.valor,
      editado: !!e,
      provisional: !!a.provisional,
      fuente: a.fuente,
      verificado: a.verificado,
      nota: e?.nota ?? null
    };
  });
}

// src/asientos.ts
function totalLinea(l) {
  return l.baseImponible + l.cuotaImpuesto;
}
function totalAsiento(a) {
  return a.lineas.reduce((s, l) => s + totalLinea(l), 0);
}
function retencionAsiento(a) {
  return a.lineas.reduce((s, l) => s + (l.retencionImporte ?? 0), 0);
}
function ingresoComputable(a) {
  if (a.concepto === null) return 0;
  return a.lineas.reduce((s, l) => s + l.baseImponible, 0);
}
function gastoDeducibleLinea(a, l) {
  if (a.concepto === null) return 0;
  const importe = l.baseImponible + (a.impuestoDeducible ? 0 : l.cuotaImpuesto);
  const pct2 = a.porcentajeDeducible ?? 1e4;
  if (pct2 === 1e4) return importe;
  const p = BigInt(importe) * BigInt(pct2);
  const q = p / 10000n;
  const r = p % 10000n;
  return Number(r * 2n >= 10000n ? q + 1n : q);
}
function gastoDeducible(a) {
  return a.lineas.reduce((s, l) => s + gastoDeducibleLinea(a, l), 0);
}

// src/modelo130.ts
function calcularModelo130(e, normativa) {
  const avisos = [];
  const explicacion = {};
  const anteriores = e.anteriores.filter((a) => a.trimestre < e.trimestre);
  if (anteriores.length !== e.anteriores.length) {
    avisos.push("Se han ignorado modelos de trimestres iguales o posteriores al que se calcula.");
  }
  const faltan = [1, 2, 3].filter((t) => t < e.trimestre && !anteriores.some((a) => a.trimestre === t));
  if (faltan.length) {
    avisos.push(`Faltan los datos del 130 de: ${faltan.map((t) => `${t}T`).join(", ")}. Las casillas 05 y 15 pueden ser incorrectas.`);
  }
  const c01 = e.ingresosAcumulados;
  let provision = 0;
  if (e.modalidad === "EDS") {
    const { porcentaje: porcentaje2, limiteAnual } = normativa.dificilJustificacion.valor;
    const rendimientoPrevio = c01 - e.gastosAcumulados;
    if (rendimientoPrevio > 0) provision = Math.min(aplicarPorcentaje(rendimientoPrevio, porcentaje2), limiteAnual);
  }
  const c02 = e.gastosAcumulados + provision;
  explicacion["01"] = "Ingresos computables acumulados desde el 1 de enero.";
  explicacion["02"] = e.modalidad === "EDS" ? `Gastos deducibles acumulados m\xE1s la provisi\xF3n por gastos de dif\xEDcil justificaci\xF3n (${normativa.dificilJustificacion.valor.porcentaje / 100} % del rendimiento positivo, m\xE1x. ${normativa.dificilJustificacion.valor.limiteAnual / 100} \u20AC/a\xF1o).` : "Gastos deducibles acumulados.";
  const c03 = c01 - c02;
  let porcentaje = normativa.pago130.valor.general;
  if (e.ambito && e.ambito !== "general") {
    const exc = normativa.pago130.valor.excepciones.find(
      (x) => x.ambito === e.ambito && x.trimestres.includes(e.trimestre)
    );
    if (exc) porcentaje = exc.porcentaje;
  }
  const c04 = c03 > 0 ? aplicarPorcentaje(c03, porcentaje) : 0;
  explicacion["03"] = "Rendimiento neto acumulado (01 \u2212 02).";
  explicacion["04"] = `${porcentaje / 100} % del rendimiento si es positivo; si no, 0.`;
  const c05 = anteriores.reduce((s, a) => s + Math.max(a.casilla07, 0), 0) - anteriores.reduce((s, a) => s + a.casilla16, 0);
  const c06 = e.retencionesAcumuladas;
  const c07 = c04 - c05 - c06;
  explicacion["05"] = "Suma de las casillas 07 positivas de los trimestres anteriores, menos sus casillas 16.";
  explicacion["06"] = "Retenciones e ingresos a cuenta acumulados.";
  explicacion["07"] = "04 \u2212 05 \u2212 06.";
  const c12 = Math.max(c07, 0);
  const procedeMinoracion = e.aplicarMinoracion ?? true;
  const c13 = procedeMinoracion ? minoracion130(normativa.minoracion130.valor, e.rendimientoNetoAnterior) : 0;
  const c14 = c12 - c13;
  explicacion["12"] = "Suma de secciones (solo Secci\xF3n I); si es negativa, 0.";
  explicacion["13"] = c13 > 0 ? `Minoraci\xF3n por rendimientos del ejercicio anterior \u2264 12.000 \u20AC (art. 110.3.c RIRPF).` : "Sin minoraci\xF3n (rendimiento del ejercicio anterior superior a 12.000 \u20AC o no aplicada).";
  explicacion["14"] = "12 \u2212 13.";
  let c15 = 0;
  if (c14 > 0) {
    const negativosPrevios = anteriores.reduce((s, a) => s + (a.casilla19 < 0 ? -a.casilla19 : 0), 0);
    const yaDeducidos = anteriores.reduce((s, a) => s + a.casilla15, 0);
    c15 = Math.min(Math.max(negativosPrevios - yaDeducidos, 0), c14);
  }
  explicacion["15"] = "Resultados negativos de trimestres anteriores a\xFAn no deducidos (sin superar la casilla 14).";
  const c16 = 0;
  explicacion["16"] = "Deducci\xF3n por vivienda habitual: no contemplada en esta versi\xF3n.";
  const c17 = c14 < 0 ? c14 : c14 - c15 - c16;
  const c18 = e.complementariaAnterior ?? 0;
  const c19 = c17 - c18;
  explicacion["17"] = "14 \u2212 15 \u2212 16.";
  explicacion["18"] = "Solo en complementarias: lo ingresado en la declaraci\xF3n anterior del mismo periodo.";
  explicacion["19"] = "Resultado de la autoliquidaci\xF3n.";
  let destino;
  if (c19 > 0) destino = "ingresar";
  else if (c19 < 0 && e.trimestre < 4) destino = "a_deducir";
  else if (c19 < 0) destino = "negativa";
  else destino = "cero";
  if (normativa.pago130.provisional || normativa.minoracion130.provisional) {
    avisos.push(`Par\xE1metros del ${normativa.ejercicio} provisionales: revisa la normativa antes de presentar.`);
  }
  return {
    casillas: {
      "01": c01,
      "02": c02,
      "03": c03,
      "04": c04,
      "05": c05,
      "06": c06,
      "07": c07,
      "12": c12,
      "13": c13,
      "14": c14,
      "15": c15,
      "16": c16,
      "17": c17,
      "18": c18,
      "19": c19
    },
    provisionDificilJustificacion: provision,
    porcentajeAplicado: porcentaje,
    destino,
    avisos,
    explicacion
  };
}

// src/libros.ts
var CABECERA_INGRESOS = [
  ["Autoliquidaci\xF3n", "", "Actividad", "", "", "Tipo de Factura", "Concepto de\nIngreso", "Ingreso Computable", "Fecha\nExpedici\xF3n", "Fecha\nOperaci\xF3n", "Identificaci\xF3n de la Factura", "", "", "NIF Destinatario", "", "", "Nombre\nDestinatario", "Clave de Operaci\xF3n", "Calificaci\xF3n de la Operaci\xF3n", "Operaci\xF3n\nExenta", "Total\nFactura", "Base\nImponible", "Tipo\nde IVA", "Cuota IVA\nRepercutida", "Tipo de\nRecargo Eq.", "Cuota Recargo Eq.", "Cobro (Operaci\xF3n Criterio de Caja de IVA y/o art\xEDculo 7.2.1\xBA de Reglamento del IRPF)", "", "", "", "Tipo Retenci\xF3n\ndel IRPF", "Importe Retenido\ndel IRPF", "Registro Acuerdo Facturaci\xF3n", "Inmueble", "", "Referencia Externa"],
  ["Ejercicio", "Periodo", "C\xF3digo", "Tipo", "Grupo o Ep\xEDgrafe del IAE", "", "", "", "", "", "Serie", "N\xFAmero", "N\xFAmero-Final", "Tipo", "C\xF3digo Pa\xEDs", "Identificaci\xF3n", "", "", "", "", "", "", "", "", "", "", "Fecha", "Importe", "Medio Utilizado", "Identificaci\xF3n Medio Utilizado", "", "", "", "Situaci\xF3n", "Referencia Catastral", ""],
  ["Decimal (4,0)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (4)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (3)", "Decimal(12,2)", "Fecha(dd/mm/yyyy)", "Fecha(dd/mm/yyyy)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (40)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (2)", "Decimal(12,2)", "Decimal(12,2)", "Decimal(4,2)", "Decimal(12,2)", "Decimal(4,2)", "Decimal(12,2)", "Fecha(dd/mm/yyyy)", "Decimal(12,2)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (34)", "Decimal(4,2)", "Decimal(12,2)", "Alfanum\xE9rico (15)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (40)"]
];
var CABECERA_GASTOS = [
  ["Autoliquidaci\xF3n", "", "Actividad", "", "", "Tipo de Factura", "Concepto de\nGasto", "Gasto Deducible", "Fecha\nExpedici\xF3n", "Fecha\nOperaci\xF3n", "Identificaci\xF3n Factura del Expedidor", "", "Fecha\nRecepci\xF3n", "N\xFAmero\nRecepci\xF3n", "N\xFAmero\nRecepci\xF3n Final", "NIF Expedidor", "", "", "Nombre\nExpedidor", "Clave de\nOperaci\xF3n", "Bien de Inversi\xF3n", "Inversi\xF3n del Sujeto Pasivo", "Deducible en Periodo Posterior", "Periodo Deducci\xF3n", "", "Total\nFactura", "Base\nImponible", "Tipo\nde IVA", "Cuota IVA\nSoportado", "Cuota Deducible", "Tipo de\nRecargo Eq.", "Cuota\nRecargo Eq.", "Pago (Operaci\xF3n Criterio de Caja de IVA y/o art\xEDculo 7.2.1\xBA de Reglamento del IRPF)", "", "", "", "Tipo Retenci\xF3n\ndel IRPF", "Importe Retenido\ndel IRPF", "Registro Acuerdo Facturaci\xF3n", "Inmueble", "", "Referencia Externa"],
  ["Ejercicio", "Periodo", "C\xF3digo", "Tipo", "Grupo o Ep\xEDgrafe del IAE", "", "", "", "", "", "(Serie-N\xFAmero)", "N\xFAmero-Final", "", "", "", "Tipo", "C\xF3digo Pa\xEDs", "Identificaci\xF3n", "", "", "", "", "", "Ejercicio", "Periodo", "", "", "", "", "", "", "", "Fecha", "Importe", "Medio Utilizado", "Identificaci\xF3n Medio Utilizado", "", "", "", "Situaci\xF3n", "Referencia Catastral", ""],
  ["Decimal (4,0)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (4)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (3)", "Decimal(12,2)", "Fecha(dd/mm/yyyy)", "Fecha(dd/mm/yyyy)", "Alfanum\xE9rico (40)", "Alfanum\xE9rico (20)", "Fecha(dd/mm/yyyy)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (40)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (1)", "Decimal (4,0)", "Alfanum\xE9rico (2)", "Decimal(12,2)", "Decimal(12,2)", "Decimal(4,2)", "Decimal(12,2)", "Decimal(12,2)", "Decimal(4,2)", "Decimal(12,2)", "Fecha(dd/mm/yyyy)", "Decimal(12,2)", "Alfanum\xE9rico (2)", "Alfanum\xE9rico (34)", "Decimal(4,2)", "Decimal(12,2)", "Alfanum\xE9rico (15)", "Alfanum\xE9rico (1)", "Alfanum\xE9rico (20)", "Alfanum\xE9rico (40)"]
];
var COLUMNAS_INGRESOS = CABECERA_INGRESOS[2].length;
var COLUMNAS_GASTOS = CABECERA_GASTOS[2].length;
var truncar = (texto, max) => {
  if (!texto) return null;
  const t = texto.trim();
  return t.length > max ? t.slice(0, max) : t;
};
var euros = (c) => aEuros(c);
var pct = (pb) => pb / 100;
var fecha = (f) => f ? { fecha: f } : null;
function columnasContraparte(c) {
  if (!c) return [null, null, null];
  const clave = CLAVE_TIPO_IDENTIFICACION[c.tipoIdentificacion];
  if (clave === "01") {
    return [null, null, truncar(c.identificacion, 20)];
  }
  return [clave, clave === "02" ? null : c.pais, truncar(c.identificacion, 20)];
}
function filasLibroIngresos(asientos, actividad) {
  const filas = [];
  const incidencias = [];
  const ordenados = [...asientos].sort((a, b) => compararFechas(a.fechaOperacion, b.fechaOperacion));
  for (const a of ordenados) {
    const err = (mensaje, nivel = "error") => incidencias.push({ nivel, libro: "INGRESOS", asientoId: a.id, mensaje });
    if (!esFechaISO(a.fechaOperacion)) {
      err("Fecha de operaci\xF3n no v\xE1lida");
      continue;
    }
    if (a.lineas.length === 0) {
      err("El asiento no tiene importes");
      continue;
    }
    const conFactura = a.tipoFactura !== "SF";
    if (conFactura && !a.numero) err("Factura sin n\xFAmero");
    if (a.tipoFactura === "F1" && !a.cliente) err("Una factura F1 debe identificar al destinatario");
    if (a.cliente) {
      const v = validarIdentificacion(a.cliente.tipoIdentificacion, a.cliente.identificacion);
      if (!v.valido) err(`Identificaci\xF3n del cliente dudosa: ${v.motivo}`, "aviso");
    }
    if (a.concepto && !CONCEPTOS_INGRESO.existe(a.concepto)) err(`Concepto de ingreso desconocido: ${a.concepto}`);
    const [tipoId, pais, identificacion] = columnasContraparte(a.cliente);
    const nombre = a.tipoFactura === "F2" ? "VENTAS A CONSUMIDOR FINAL" : truncar(a.cliente?.nombre, 40);
    const total = totalAsiento(a);
    a.lineas.forEach((l, i) => {
      filas.push([
        ejercicioDe(a.fechaOperacion),
        // A Ejercicio
        periodoDe(a.fechaOperacion),
        // B Periodo
        actividad.codigo,
        // C Código actividad
        actividad.tipo,
        // D Tipo actividad
        actividad.epigrafe,
        // E Epígrafe
        a.tipoFactura,
        // F Tipo de factura
        a.concepto,
        // G Concepto de ingreso
        a.concepto ? euros(l.baseImponible) : null,
        // H Ingreso computable
        fecha(conFactura ? a.fechaExpedicion ?? a.fechaOperacion : null),
        // I Fecha expedición
        fecha(a.fechaOperacion),
        // J Fecha operación
        conFactura ? truncar(a.serie, 20) : null,
        // K Serie
        conFactura ? truncar(a.numero, 20) : null,
        // L Número
        null,
        // M Número final
        tipoId,
        // N Tipo identificación
        pais,
        // O Código país
        identificacion,
        // P Identificación
        nombre,
        // Q Nombre destinatario
        null,
        // R Clave de operación (solo IVA)
        null,
        // S Calificación (solo IVA)
        null,
        // T Operación exenta (solo IVA)
        i === 0 ? euros(total) : null,
        // U Total factura (una vez por asiento)
        euros(l.baseImponible),
        // V Base imponible
        pct(l.tipoImpuesto),
        // W Tipo
        euros(l.cuotaImpuesto),
        // X Cuota
        null,
        // Y Tipo recargo
        null,
        // Z Cuota recargo
        null,
        null,
        null,
        null,
        // AA-AD Cobro (criterio de caja)
        l.retencionTipo ? pct(l.retencionTipo) : null,
        // AE Tipo retención
        l.retencionImporte ? euros(l.retencionImporte) : null,
        // AF Importe retenido
        null,
        // AG Registro acuerdo facturación
        null,
        null,
        // AH-AI Inmueble
        truncar(a.referenciaExterna, 40)
        // AJ Referencia externa
      ]);
    });
  }
  return { filas, incidencias };
}
function filasLibroGastos(asientos, actividad) {
  const filas = [];
  const incidencias = [];
  const fechaRef = (a) => a.fechaOperacion ?? a.fechaExpedicion;
  const ordenados = [...asientos].sort((a, b) => compararFechas(fechaRef(a), fechaRef(b)));
  for (const a of ordenados) {
    const err = (mensaje, nivel = "error") => incidencias.push({ nivel, libro: "GASTOS", asientoId: a.id, mensaje });
    if (!esFechaISO(a.fechaExpedicion)) {
      err("Fecha de expedici\xF3n no v\xE1lida");
      continue;
    }
    if (a.lineas.length === 0) {
      err("El asiento no tiene importes");
      continue;
    }
    if (a.tipoFactura !== "SF" && !a.proveedor) err("Falta el proveedor", "aviso");
    if (a.tipoFactura !== "SF" && !a.numeroFactura) err("Falta el n\xFAmero de factura", "aviso");
    if (a.proveedor) {
      const v = validarIdentificacion(a.proveedor.tipoIdentificacion, a.proveedor.identificacion);
      if (!v.valido) err(`Identificaci\xF3n del proveedor dudosa: ${v.motivo}`, "aviso");
    }
    if (a.concepto && !CONCEPTOS_GASTO.existe(a.concepto)) err(`Concepto de gasto desconocido: ${a.concepto}`);
    const ref = fechaRef(a);
    const [tipoId, pais, identificacion] = columnasContraparte(a.proveedor);
    const total = totalAsiento(a);
    a.lineas.forEach((l, i) => {
      const integraCuota = !a.impuestoDeducible && l.cuotaImpuesto !== 0;
      const base = integraCuota ? l.baseImponible + l.cuotaImpuesto : l.baseImponible;
      const deducible = gastoDeducibleLinea(a, l);
      filas.push([
        ejercicioDe(ref),
        // A Ejercicio
        periodoDe(ref),
        // B Periodo
        actividad.codigo,
        // C
        actividad.tipo,
        // D
        actividad.epigrafe,
        // E
        a.tipoFactura,
        // F Tipo de factura
        a.concepto,
        // G Concepto de gasto
        a.concepto ? euros(deducible) : null,
        // H Gasto deducible
        fecha(a.fechaExpedicion),
        // I Fecha expedición
        fecha(a.fechaOperacion ?? null),
        // J Fecha operación
        truncar(a.numeroFactura, 40),
        // K Serie-Número
        null,
        // L Número final
        null,
        // M Fecha recepción (solo IVA)
        null,
        // N Número recepción (solo IVA)
        null,
        // O Número recepción final
        tipoId,
        // P Tipo identificación
        pais,
        // Q Código país
        identificacion,
        // R Identificación
        truncar(a.proveedor?.nombre, 40),
        // S Nombre expedidor
        null,
        // T Clave operación (solo IVA)
        null,
        // U Bien de inversión (solo IVA)
        null,
        // V Inversión sujeto pasivo (solo IVA)
        null,
        // W Deducible en periodo posterior
        null,
        null,
        // X-Y Periodo deducción
        i === 0 ? euros(total) : null,
        // Z Total factura
        euros(base),
        // AA Base imponible
        integraCuota ? 0 : pct(l.tipoImpuesto),
        // AB Tipo
        integraCuota ? 0 : euros(l.cuotaImpuesto),
        // AC Cuota soportada
        integraCuota ? 0 : euros(a.impuestoDeducible ? l.cuotaImpuesto : 0),
        // AD Cuota deducible
        null,
        // AE Tipo recargo
        null,
        // AF Cuota recargo
        null,
        null,
        null,
        null,
        // AG-AJ Pago (criterio de caja)
        l.retencionTipo ? pct(l.retencionTipo) : null,
        // AK Tipo retención
        l.retencionImporte ? euros(l.retencionImporte) : null,
        // AL Importe retenido
        null,
        // AM Registro acuerdo facturación
        null,
        null,
        // AN-AO Inmueble
        truncar(a.referenciaExterna, 40)
        // AP Referencia externa
      ]);
    });
  }
  return { filas, incidencias };
}
function nombreFicheroLibros(ejercicio, titular) {
  const nombre = titular.nombre.trim().replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, " ");
  return `${ejercicio}${titular.nif.toUpperCase()}D${nombre}.xlsx`;
}
function asientosHasta(asientos, fechaDe, ejercicio, hasta) {
  return asientos.filter((a) => {
    const f = fechaDe(a);
    return ejercicioDe(f) === ejercicio && compararFechas(f, hasta) <= 0;
  });
}

// src/repep.ts
function volumenOperacionesRepep(ejercicio, ingresos, altaActividad, bajaActividad, umbralSiguienteEditado) {
  const inicio = altaActividad && ejercicioDe(altaActividad) === ejercicio ? altaActividad : `${ejercicio}-01-01`;
  const fin = bajaActividad && ejercicioDe(bajaActividad) === ejercicio ? bajaActividad : `${ejercicio}-12-31`;
  const total = diasDelAnio(ejercicio);
  const dias = Math.max(diasEntre(inicio, fin) + 1, 1);
  const anualizado = dias >= total ? ingresos : Math.round(ingresos * total / dias);
  const umbral = umbralSiguienteEditado ?? normativaDe(ejercicio + 1).umbralRepep.valor;
  return {
    ejercicio,
    volumenReal: ingresos,
    volumenAnualizado: anualizado,
    diasActividad: dias,
    umbralSiguiente: umbral,
    porcentajeUmbral: umbral > 0 ? Math.round(anualizado * 1e4 / umbral) : 0,
    superaUmbral: anualizado > umbral
  };
}

// src/calendario.ts
function plazo(anio, mes, diaDesde, diaHasta) {
  const fin = diaHasta === "fin" ? diasDelMes(anio, mes) : diaHasta;
  return [crearFecha(anio, mes, diaDesde), trasladarFinDeSemana(crearFecha(anio, mes, fin))];
}
function obligacionesDelEjercicio(ejercicio, perfil) {
  const lista = [];
  if ((perfil.regimenIrpf === "EDS" || perfil.regimenIrpf === "EDN") && !perfil.exoneradoPago130) {
    const trimestres = [
      ["1T", ejercicio, 4, 1, 20],
      ["2T", ejercicio, 7, 1, 20],
      ["3T", ejercicio, 10, 1, 20],
      ["4T", ejercicio + 1, 1, 1, 30]
    ];
    for (const [t, anio, mes, d1, d2] of trimestres) {
      const [desde, hasta] = plazo(anio, mes, d1, d2);
      lista.push({
        clave: `130-${ejercicio}-${t}`,
        modelo: "130",
        organismo: "AEAT",
        ejercicio,
        periodo: t,
        titulo: `Modelo 130 \xB7 ${t} ${ejercicio} (pago fraccionado IRPF)`,
        desde,
        hasta,
        condicional: false,
        nota: "Se presenta tambi\xE9n aunque el resultado sea negativo o cero."
      });
    }
  }
  if (perfil.regimenIrpf === "EO") {
    lista.push({
      clave: `131-${ejercicio}`,
      modelo: "131",
      organismo: "AEAT",
      ejercicio,
      periodo: "0A",
      titulo: `Modelo 131 \xB7 ${ejercicio} (estimaci\xF3n objetiva)`,
      desde: crearFecha(ejercicio, 4, 1),
      hasta: crearFecha(ejercicio + 1, 1, 30),
      condicional: false,
      nota: "R\xE9gimen no soportado todav\xEDa por la aplicaci\xF3n."
    });
  }
  {
    const [desde, hasta] = plazo(ejercicio + 1, 1, 1, "fin");
    lista.push({
      clave: `425-${ejercicio}`,
      modelo: "425",
      organismo: "ATC",
      ejercicio,
      periodo: "0A",
      titulo: perfil.regimenIgic === "REPEP" ? `Modelo 425 \xB7 ${ejercicio} (volumen de operaciones REPEP)` : `Modelo 425 \xB7 ${ejercicio} (resumen anual IGIC)`,
      desde,
      hasta,
      condicional: false,
      nota: perfil.regimenIgic === "REPEP" ? "Solo el apartado de volumen de operaciones (casillas 142\u2013147)." : void 0
    });
  }
  lista.push({
    clave: `100-${ejercicio}`,
    modelo: "100",
    organismo: "AEAT",
    ejercicio,
    periodo: "0A",
    titulo: `Renta ${ejercicio} (modelo 100)`,
    desde: crearFecha(ejercicio + 1, 4, 1),
    hasta: trasladarFinDeSemana(crearFecha(ejercicio + 1, 6, 30)),
    condicional: false,
    nota: "La fecha de inicio de la campa\xF1a es aproximada; se confirma cada a\xF1o."
  });
  {
    const [desde, hasta] = plazo(ejercicio + 1, 2, 1, "fin");
    lista.push({
      clave: `347-${ejercicio}`,
      modelo: "347",
      organismo: "AEAT",
      ejercicio,
      periodo: "0A",
      titulo: `Modelo 347 \xB7 ${ejercicio} (operaciones con terceros)`,
      desde,
      hasta,
      condicional: true,
      nota: "Solo si con alg\xFAn cliente o proveedor se superan 3.005,06 \u20AC en el a\xF1o. Pendiente de verificar si en Canarias aplica adem\xE1s/en su lugar el modelo 415 de la ATC."
    });
  }
  return lista;
}
export {
  CABECERA_GASTOS,
  CABECERA_INGRESOS,
  CATEGORIAS_DOCUMENTO,
  CLAVE_TIPO_IDENTIFICACION,
  CODIGOS_ACTIVIDAD,
  COLUMNAS_GASTOS,
  COLUMNAS_INGRESOS,
  CONCEPTOS_GASTO,
  CONCEPTOS_INGRESO,
  EJERCICIOS_CON_NORMATIVA,
  FORMAS_PAGO,
  ImporteInvalidoError,
  PARAMETROS_EDITABLES,
  TIPOS_ACTIVIDAD_A,
  TIPOS_FACTURA_EMITIDA,
  TIPOS_FACTURA_RECIBIDA,
  aCentimos,
  aDDMMAAAA,
  aEuros,
  aPuntosBasicos,
  aplicarPorcentaje,
  aplicarValoresEditados,
  asientosHasta,
  calcularModelo130,
  compararFechas,
  crearFecha,
  descriptorParametro,
  desdeDDMMAAAA,
  desdeSerieExcel,
  diaSemana,
  diasDelAnio,
  diasDelMes,
  diasEntre,
  ejercicioDe,
  esActividadProfesional,
  esCentimos,
  esFechaISO,
  filasLibroGastos,
  filasLibroIngresos,
  finTrimestre,
  formatearEuros,
  gastoDeducible,
  gastoDeducibleLinea,
  hoy,
  ingresoComputable,
  inicioTrimestre,
  interpretarFormaPago,
  minoracion130,
  nombreFicheroLibros,
  normalizarIdentificador,
  normativaDe,
  obligacionesDelEjercicio,
  periodoDe,
  periodoDeTrimestre,
  retencionAsiento,
  sumar,
  sumarDias,
  tablaParametros,
  totalAsiento,
  totalLinea,
  trasladarFinDeSemana,
  trimestreDe,
  validarIdentificacion,
  validarNifEspanol,
  validarNifIva,
  validarValorParametro,
  volumenOperacionesRepep
};
